import React from 'react';
import usePermissions from '../hooks/usePermissions';

interface PermissionGateProps {
  module: string;
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean; // If true, requires all permissions; if false, requires any permission
  permissions?: string[]; // Array of permissions to check
}

const PermissionGate: React.FC<PermissionGateProps> = ({
  module,
  permission,
  children,
  fallback = null,
  requireAll = false,
  permissions = []
}) => {
  const { hasPermission, canAccessModule } = usePermissions();

  // Check if user can access the module at all
  const canAccess = canAccessModule(module);
  
  if (!canAccess) {
    return <>{fallback}</>;
  }

  // If multiple permissions are provided
  if (permissions.length > 0) {
    const hasAnyPermission = permissions.some(perm => hasPermission(module, perm));
    const hasAllPermissions = permissions.every(perm => hasPermission(module, perm));
    
    const hasRequiredPermissions = requireAll ? hasAllPermissions : hasAnyPermission;
    
    return hasRequiredPermissions ? <>{children}</> : <>{fallback}</>;
  }

  // Single permission check
  const hasRequiredPermission = hasPermission(module, permission);
  
  return hasRequiredPermission ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGate;